/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.sprudel.ui

import io.peekandpoke.klang.ui.HoverPopupCtrl
import io.peekandpoke.klang.ui.KlangUiToolContext
import io.peekandpoke.klang.ui.KlangUiToolEmbeddable
import io.peekandpoke.klang.ui.codetools.KlangToolAutoUpdate
import io.peekandpoke.klang.ui.feel.KlangTheme
import io.peekandpoke.klang.ui.svgLine
import io.peekandpoke.klang.ui.svgPolyline
import io.peekandpoke.klang.ui.svgRect
import io.peekandpoke.klang.ui.svgRoot
import io.peekandpoke.klang.ui.svgText
import io.peekandpoke.kraft.components.Component
import io.peekandpoke.kraft.components.Ctx
import io.peekandpoke.kraft.components.comp
import io.peekandpoke.kraft.forms.formController
import io.peekandpoke.kraft.popups.PopupsManager.Companion.popups
import io.peekandpoke.kraft.semanticui.forms.UiInputField
import io.peekandpoke.kraft.vdom.VDom
import io.peekandpoke.ultra.html.css
import io.peekandpoke.ultra.html.key
import io.peekandpoke.ultra.html.onClick
import io.peekandpoke.ultra.semanticui.SemanticIconFn
import io.peekandpoke.ultra.semanticui.icon
import io.peekandpoke.ultra.semanticui.ui
import kotlinx.css.Cursor
import kotlinx.css.cursor
import kotlinx.css.marginBottom
import kotlinx.css.minWidth
import kotlinx.css.px
import kotlinx.css.rem
import kotlinx.html.FlowContent
import kotlinx.html.Tag
import kotlinx.html.div
import kotlin.math.PI
import kotlin.math.sin

// ── Tool singleton ────────────────────────────────────────────────────────────

/**
 * [KlangUiToolEmbeddable] for the per-param phaser(wet, rate, center, sweep) call.
 *
 * Two modes (C0.3 two-tool-tier design):
 * - Whole-call modal: when [KlangUiToolContext.call] is present, edits rate plus the optional
 *   wet/center/sweep params of the host call and commits the full argument list. Unset
 *   optionals stay omitted (null slots). The indices are the door's parameter order, wet first
 *   (step 3d(iii), 2026-09-24); the tool stays attached to `rate`.
 * - Scalar fallback (embedded / sequence atom): edits a single rate value.
 */
object SprudelPhaserEditorTool : KlangUiToolEmbeddable {
    override val title: String = "Phaser Editor"

    override val iconFn: SemanticIconFn = { sync_alternate }

    override fun FlowContent.render(ctx: KlangUiToolContext) {
        SprudelPhaserEditorComp(ctx, embedded = false)
    }

    override fun FlowContent.renderEmbedded(ctx: KlangUiToolContext) {
        SprudelPhaserEditorComp(ctx, embedded = true)
    }
}

// ── Entry-point helpers ───────────────────────────────────────────────────────

@Suppress("FunctionName")
private fun Tag.SprudelPhaserEditorComp(toolCtx: KlangUiToolContext, embedded: Boolean) =
    comp(SprudelPhaserEditorComp.Props(toolCtx, embedded)) { SprudelPhaserEditorComp(it) }

// ── Component ─────────────────────────────────────────────────────────────────

private class SprudelPhaserEditorComp(ctx: Ctx<Props>) : Component<SprudelPhaserEditorComp.Props>(ctx) {

    data class Props(val toolCtx: KlangUiToolContext, val embedded: Boolean = false)

    private companion object {
        // The door's parameter indices: `phaser(wet, rate, center, sweep, floor)`.
        const val WET = 0
        const val RATE = 1
        const val CENTER = 2
        const val SWEEP = 3
    }

    // ── Parse current value from raw source text ──────────────────────────────

    private val laf by subscribingTo(KlangTheme)
    private val autoUpdate by subscribingTo(KlangToolAutoUpdate)

    private val infoPopup = HoverPopupCtrl(popups)

    private val formCtrl = formController()

    private val call = props.toolCtx.call

    private val initialValue = props.toolCtx.currentValue ?: ""

    // Whole-call mode reads the params from the host call's args, by the door's parameter index
    // (wet, rate, center, sweep); scalar mode reads the single arg, which is the rate.
    private val parsedDepth
        get() = parseNumOrNull(call?.args?.getOrNull(WET))

    private val parsedRate
        get() = parseNum(call?.args?.getOrNull(RATE) ?: initialValue, 0.5)

    private val parsedCenter
        get() = parseNumOrNull(call?.args?.getOrNull(CENTER))

    private val parsedSweep
        get() = parseNumOrNull(call?.args?.getOrNull(SWEEP))

    private var rate by value(parsedRate)
    private var depth by value(parsedDepth)
    private var center by value(parsedCenter)
    private var sweep by value(parsedSweep)

    // Slot bookkeeping: an untouched arg that fails the parse (pattern, variable, expression)
    // must never be overwritten, and untouched absent slots stay absent (engine defaults apply).
    private val parseable: List<Boolean> = List(4) { parseNumOrNull(call?.args?.getOrNull(it)) != null }
    private val dirty = mutableSetOf<Int>()
    private var hasCommitted = false

    private var resetCounter by value(0)

    // ── Helpers ───────────────────────────────────────────────────────────────

    private fun buildValue(): String =
        if (call != null) {
            "${depth?.formatArg() ?: "-"}, ${rate.formatArg()}, ${center?.formatArg() ?: "-"}, ${sweep?.formatArg() ?: "-"}"
        } else {
            rate.formatArg()
        }

    /**
     * Writes a slot only when that is safe: the user touched it, or the original arg parses
     * (rewriting it loses nothing). Untouched non-parseable args are preserved; untouched
     * absent slots stay absent so the engine defaults apply.
     */
    private fun put(texts: MutableList<String?>, index: Int, text: String?) {
        val original = call?.args?.getOrNull(index)
        if (index in dirty || (original != null && parseable[index])) {
            texts[index] = text
        }
    }

    private fun commitValue() {
        val c = call
        if (c != null) {
            val texts = c.args.toMutableList()
            while (texts.size < 4) texts.add(null)
            put(texts, WET, depth?.formatArg())
            put(texts, RATE, rate.formatArg())
            put(texts, CENTER, center?.formatArg())
            put(texts, SWEEP, sweep?.formatArg())
            c.onCommitCall(texts)
        } else {
            props.toolCtx.onCommit(rate.formatArg())
        }
        hasCommitted = true
        lastCommitted = buildValue()
    }

    // Built-state fingerprints: in whole-call mode [initialValue] is only the clicked arg's
    // text, so the Reset/Update buttons compare built snapshots instead (initial state and
    // last committed state); scalar mode keeps the plain text comparison.
    private val initialBuiltValue = buildValue()
    private var lastCommitted = initialBuiltValue

    private val isInitialModified
        get() = if (call != null) buildValue() != initialBuiltValue else initialValue != buildValue()

    private val isCurrentModified
        get() = if (call != null) buildValue() != lastCommitted else (props.toolCtx.currentValue ?: "") != buildValue()

    private fun liveUpdate() {
        if (props.embedded || autoUpdate) {
            commitValue()
        }
    }

    private fun onCancel() {
        if (!props.embedded && autoUpdate && hasCommitted && isInitialModified) {
            val c = call
            if (c != null) c.onCommitCall(c.args) else props.toolCtx.onCommit(initialValue)
        }
        props.toolCtx.onCancel()
    }

    private fun onReset() {
        dirty.clear()
        rate = parsedRate
        depth = parsedDepth
        center = parsedCenter
        sweep = parsedSweep
        formCtrl.resetAllFields()
        commitValue()
        resetCounter++
    }

    private fun onCommit() {
        commitValue()
    }

    // ── Render ────────────────────────────────────────────────────────────────

    override fun VDom.render() {
        if (props.embedded) {
            renderContent()
        } else {
            ui.segment {
                css { minWidth = 400.px }
                toolHeaderWithInfo("Phaser", props.toolCtx, infoPopup)
                renderContent()
                ui.divider {}
                ToolButtonBar(
                    isInitialModified = isInitialModified,
                    isCurrentModified = isCurrentModified,
                    onCancel = ::onCancel,
                    onReset = ::onReset,
                    onCommit = ::onCommit,
                )
            }
        }
    }

    private fun FlowContent.renderContent() {
        div {
            key = "phaser-editor-content-$resetCounter"

            ui.form {
                ui.two.stackable.fields {
                    UiInputField(rate, { rate = it; dirty += RATE; liveUpdate() }) {
                        domKey("rate")
                        step(0.1)
                        label {
                            +"Rate (Hz)"
                            paramInfoIcon("rate", props.toolCtx, infoPopup)
                        }
                    }
                    if (call != null) {
                        nullableField("depth", "Wet", 0.01, depth, subField = "wet") { depth = it; dirty += WET; liveUpdate() }
                    }
                }
                if (call != null) {
                    ui.two.stackable.fields {
                        nullableField("center", "Center (Hz)", 10.0, center, subField = "center") { center = it; dirty += CENTER; liveUpdate() }
                        nullableField("sweep", "Sweep (Hz)", 10.0, sweep, subField = "sweep") { sweep = it; dirty += SWEEP; liveUpdate() }
                    }
                }
            }
            ui.divider {}
            div {
                css { if (!props.embedded) marginBottom = 1.rem }
                renderPhaserViz()
            }
        }
    }

    private fun FlowContent.nullableField(
        key: String,
        labelText: String,
        stepVal: Double,
        current: Double?,
        subField: String? = null,
        onChange: (Double?) -> Unit,
    ) {
        UiInputField.nullable(current, { onChange(it) }) {
            domKey(key)
            step(stepVal)
            if (subField != null) {
                label {
                    +labelText
                    paramInfoIcon(subField, props.toolCtx, infoPopup)
                }
            } else {
                label(labelText)
            }
            if (current == null) {
                placeholder("default")
            }
            rightLabel {
                ui.basic.icon.label {
                    css { cursor = Cursor.pointer }
                    onClick {
                        if (current != null) onChange(null) else onChange(0.0)
                    }
                    if (current != null) icon.times() else icon.plus()
                }
            }
        }
    }

    // ── SVG visualization ────────────────────────────────────────────────────

    private fun FlowContent.renderPhaserViz() {
        val w = 400.0
        val h = 90.0
        val padL = 22.0
        val padR = 6.0
        val padT = 6.0
        val padB = 20.0
        val drawW = w - padL - padR
        val drawH = h - padT - padB

        val clampedRate = rate.coerceIn(0.01, 20.0)
        val clampedDepth = (depth ?: 0.5).coerceIn(0.0, 1.0)
        val clampedCenter = (center ?: 1000.0).coerceIn(100.0, 10000.0)
        val clampedSweep = (sweep ?: 500.0).coerceIn(0.0, 10000.0)
        val goldHex = laf.gold

        val numPoints = drawW.toInt()

        // Frequency sweep curve
        val freqPoints = buildString {
            for (i in 0 until numPoints) {
                val t = i.toDouble() / numPoints  // 0..1 (one cycle)
                val lfo = sin(t * clampedRate * 2.0 * PI)
                val freq = clampedCenter + lfo * clampedSweep * clampedDepth
                val normFreq = ((freq - 100.0) / 9900.0).coerceIn(0.0, 1.0)
                val px = padL + i.toDouble()
                val py = padT + drawH - normFreq * drawH
                if (i > 0) append(" ")
                append("$px,$py")
            }
        }

        svgRoot(viewBox = "0 0 $w $h") {
            svgRect(padL, padT, drawW, drawH, fill = "rgba(0,0,0,0.2)", rx = "2")

            // Center frequency line
            val centerNorm = ((clampedCenter - 100.0) / 9900.0).coerceIn(0.0, 1.0)
            val centerY = padT + drawH - centerNorm * drawH
            svgLine(padL, centerY, padL + drawW, centerY, stroke = "rgba(255,255,255,0.15)", strokeWidth = "0.5")

            // Frequency sweep
            svgPolyline(
                points = freqPoints,
                stroke = goldHex,
                strokeWidth = "1.5",
                strokeLinejoin = "round",
                strokeLinecap = "round",
            )

            // Axis labels
            svgText(
                x = 4, y = padT + drawH / 2,
                text = "Freq", fill = "#ccc", fontSize = "5", textAnchor = "middle",
                transform = "rotate(-90, 4, ${padT + drawH / 2})",
            )
            svgText(padL + drawW / 2, h - 4, "Time", fill = "#ccc", fontSize = "5", textAnchor = "middle")
        }
    }
}
